import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router';

// Images
import SlackLogo from 'Shared/images/slackLogo.svg';

// Utils
import PageUrlsUtil from 'Shared/utils/PageUrls.utils';

// Hooks
import { useAiSuggestionWorkspaceItems } from 'Shared/utils/hooks/use-ai-suggestion-workspace-items';
import { useWorkspaceAgendaAi } from 'Shared/utils/hooks/use-workspace-agenda-ai';
import { useWorkspace } from 'Shared/utils/hooks/use-workspace';

// Components
import { WpContainer, WpContainerBody } from 'Wpds/components/container';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpBadge } from 'Wpds/components/badge';
import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import { WpDropdown, WpDropdownMenu, WpDropdownToggle, WpDropdownMenuItem } from 'Wpds/components/dropdown';
import AgendaAiCarousel from './partials/AgendaAiCarousel';
import { WpCollapsible } from 'Wpds/components/collapsible';
import TypingDots from 'Shared/components/TypingDots/TypingDots';
import EmptyStateContainer from 'Shared/components/EmptyStateContainer/EmptyStateContainer';
import { WpTooltip, WpTooltipContent, WpTooltipTrigger } from 'Wpds/components/tooltip';

// Styles
import './AgendaAiSuggestedTopics.scss';

const AgendaAiSuggestedTopics = () => {
  const history = useHistory();

  const { id: workspaceId } = useWorkspace();

  const {
    isFetching,
    isRegenerating,
    suggestions,
    handleRefreshSuggestions,
    handleAcceptAiSuggestion,
    handleRejectAiSuggestion,
  } = useAiSuggestionWorkspaceItems();

  const {
    selectedChannelIds,
    isDiscussionTopicPanelOpen,
    anyAiChatScanRequests,
    handleUpdateWorkspaceAiSetting,
  } = useWorkspaceAgendaAi({
    fetchAvailableChannels: false,
  });

  const [channelIds, setChannelIds] = useState(selectedChannelIds);

  const showEmptyState = suggestions.length === 0 && !isRegenerating;

  const handleRefreshClick = useCallback(() => {
    handleRefreshSuggestions();
  }, []);

  const handleSelectChannelsClick = useCallback(() => {
    history.push({
      pathname: PageUrlsUtil.makeWorkspaceSettingsUrl(workspaceId),
      search: '?show=agenda_ai',
    });
  }, []);

  const handleMinimizeMaximize = () => {
    handleUpdateWorkspaceAiSetting({ discussion_topic_panel_open: !isDiscussionTopicPanelOpen });
  };

  const handleMinimizeMaximizeButtonClick = e => {
    e.stopPropagation();
    handleMinimizeMaximize();
  };

  useEffect(() => {
    const isEqual =
      Array.isArray(channelIds) &&
      Array.isArray(selectedChannelIds) &&
      channelIds.length === selectedChannelIds.length &&
      channelIds.every((val, index) => val === selectedChannelIds[index]);

    if (
      (!isEqual && !isFetching && !isRegenerating) ||
      (!anyAiChatScanRequests && !isFetching && !isRegenerating && suggestions.length === 0)
    ) {
      handleRefreshClick();
      setChannelIds(selectedChannelIds);
    }
  }, [selectedChannelIds, isFetching, isRegenerating, suggestions, anyAiChatScanRequests]);

  const baseClassName = 'AgendaAiSuggestedTopics';

  return (
    <WpContainer className={baseClassName} onClick={!isDiscussionTopicPanelOpen ? handleMinimizeMaximize : null}>
      <WpContainerBody>
        <div className={`${baseClassName}__header`}>
          <div className={`${baseClassName}__header__title`}>
            <img src={SlackLogo} alt="Slack Logo" className={`${baseClassName}__header__title__logo`} />
            <p className={`${baseClassName}__header__title__label`}>
              AI Suggested Topics from Slack
              <span className={`${baseClassName}__header__title__label__badge`}>
                <WpBadge color="purple" variant="bold">
                  Beta
                </WpBadge>
              </span>
            </p>
          </div>
          <WpButtonGroup spacing="md">
            {isDiscussionTopicPanelOpen && (
              <WpTooltip>
                <WpTooltipTrigger>
                  <WpButton
                    variant="tertiary"
                    onClick={handleRefreshClick}
                    loading={isRegenerating}
                    disabled={isFetching}
                  >
                    <WpIcon name="redo" />
                  </WpButton>
                </WpTooltipTrigger>
                <WpTooltipContent> Generate new topics</WpTooltipContent>
              </WpTooltip>
            )}
            {isDiscussionTopicPanelOpen && (
              <WpDropdown placement="bottom-end" disabled={isRegenerating || isFetching}>
                <WpDropdownToggle>
                  <WpButton variant="tertiary">
                    <WpIcon name="cog" />
                  </WpButton>
                </WpDropdownToggle>
                <WpDropdownMenu>
                  <WpDropdownMenuItem onClick={handleSelectChannelsClick}>
                    Edit connected Slack channels
                  </WpDropdownMenuItem>
                </WpDropdownMenu>
              </WpDropdown>
            )}
            <WpTooltip>
              <WpTooltipTrigger>
                <WpButton variant="tertiary" onClick={handleMinimizeMaximizeButtonClick}>
                  <WpIcon name={isDiscussionTopicPanelOpen ? 'minus' : 'plus'} />
                </WpButton>
              </WpTooltipTrigger>
              <WpTooltipContent>{isDiscussionTopicPanelOpen ? 'Minimize' : 'View suggestions'}</WpTooltipContent>
            </WpTooltip>
          </WpButtonGroup>
        </div>
        <WpCollapsible open={isDiscussionTopicPanelOpen}>
          <div className={`${baseClassName}__suggestions`}>
            <AgendaAiCarousel
              aiSuggestions={suggestions}
              handleAcceptAiSuggestion={handleAcceptAiSuggestion}
              handleRejectAiSuggestion={handleRejectAiSuggestion}
            />
          </div>
          {isFetching && !isRegenerating && (
            <EmptyStateContainer subTitle="Fetching suggested discussion topics..." inContainer={false} padding={24} />
          )}
          {isRegenerating && !isFetching && (
            <div className={`${baseClassName}__generating`}>
              <div className={`${baseClassName}__generating__header`}>
                <TypingDots />
                <span className={`${baseClassName}__generating__header__label`}>
                  wrangling slack threads, give us a minute...
                </span>
              </div>
            </div>
          )}
          {!isFetching && showEmptyState && (
            <EmptyStateContainer
              subTitle="There aren’t any suggestions left"
              inContainer={false}
              padding={24}
              button={
                <WpButton variant="tertiary" onClick={handleRefreshClick} loading={isRegenerating}>
                  <WpIcon name="redo" /> Generate New Topics
                </WpButton>
              }
            />
          )}
        </WpCollapsible>
      </WpContainerBody>
    </WpContainer>
  );
};

export default AgendaAiSuggestedTopics;
